import { createFileRoute, Link, notFound, useParams } from "@tanstack/react-router";

import { Footer, Nav } from "../components/lidoo/chrome";
import { ClubsView } from "../components/lidoo/clubs-view";
import { CoverImage } from "../components/lidoo/cover-image";
import { RegionAnswers } from "../components/lidoo/region-answers";
import { ThemeLinks } from "../components/lidoo/theme-page";
import {
  listClubsByRegion,
  regionFacts,
  regionPhoto,
  type Club,
  type RegionFacts,
  type RegionPhoto,
} from "../lib/api/lidoo.functions";
import { REGION_CARDS } from "../lib/geo";
import { DICTS, fill, regionName, useI18n } from "../lib/i18n";
import { coverSources, type CoverSource } from "../lib/place-photos";
import { NOINDEX_META, regionIsIndexable } from "../lib/indexing";
import { catalogueHreflang } from "../lib/seo";

export const Route = createFileRoute("/region/$key")({
  loader: async ({ params }) =>
    await Promise.all([
      listClubsByRegion({ data: { regionKey: params.key } }),
      regionFacts({ data: { regionKey: params.key } }),
      regionPhoto({ data: { regionKey: params.key } }),
    ]).then(([list, facts, hero]) => {
      // A region key nobody has clubs under is not a page. /region/nope used to
      // answer 200 with "Beach clubs in nope" and a self-canonical: a soft 404
      // that any mistyped or invented URL could mint into the index.
      if (list.clubs.length === 0) throw notFound();
      return { ...list, facts, hero };
    }),
  head: (ctx) => {
    const d = DICTS["en"];
    const stored = ctx.loaderData?.regionLabel ?? "";
    const label = regionName(ctx.params.key, stored, (k) => d[k] ?? k);
    const count = ctx.loaderData?.clubs.length ?? 0;
    return {
      meta: [
        { title: `${fill(d.region_meta_title, { place: label })} | sunbedly` },
        {
          name: "description",
          content: fill(d.region_meta_desc, { place: label, n: String(count) }),
        },
        // A coast with a handful of clubs is a heading and a few cards; it
        // stays online but out of the index until it fills up (lib/indexing.ts).
        ...(regionIsIndexable(count) ? [] : [NOINDEX_META]),
      ],
      links: catalogueHreflang(`/region/${ctx.params.key}`),
    };
  },
  component: () => <RegionPage {...Route.useLoaderData()} />,
});

// Props rather than Route.useLoaderData(), so /nl/region/... renders this same
// page from its own loader instead of the English route's data.
export function RegionPage({
  clubs,
  regionLabel,
  country,
  facts,
  hero,
}: {
  clubs: Club[];
  regionLabel: string;
  country: string;
  facts?: RegionFacts | null;
  hero?: RegionPhoto | null;
}) {

  const { t } = useI18n();
  const { key } = useParams({ strict: false }) as { key?: string };
  // The database stores the English name; the reader gets their own.
  const label = key ? regionName(key, regionLabel, t) : regionLabel;
  const cardMeta = REGION_CARDS.find((r) => r.label === regionLabel);
  // The banner, in order. A photograph chosen by hand from Pexels for this
  // coast (region_photos, source 'pexels') comes first, above the generated
  // artwork the six European coasts had: it is a real beach on this coast,
  // stored for good and free per view. Then that artwork. Then the coast's
  // Creative Commons hero, if it has one (a CC photo of this coastline,
  // found by coordinates). Then a real photo of the coast's strongest club
  // (own before Google, same order as the cards).
  const clubPhoto = clubs.map((c) => coverSources(c, 800)[0]).find(Boolean);
  const pexelsHero = hero?.source === "pexels" ? hero : null;
  const ccHero = hero && hero.source !== "pexels" ? hero : null;
  // The stock lido shot is the last resort, and also what the hero falls back
  // to if the photo before it fails to load, so the banner is never broken.
  const heroSources: CoverSource[] = [
    ...(pexelsHero ? [{ src: pexelsHero.src, kind: "licensed" as const }] : []),
    ...(cardMeta?.image ? [{ src: cardMeta.image, kind: "own" as const }] : []),
    ...(ccHero ? [{ src: ccHero.src, kind: "licensed" as const }] : []),
    ...(clubPhoto ? [clubPhoto] : []),
    { src: "/assets/lido-side-800.webp", kind: "own" },
  ];
  const first = pexelsHero ?? (cardMeta?.image ? null : ccHero);
  // CC BY / CC BY-SA ask for the name wherever the picture is shown. This is
  // only rendered when the CC hero is the first candidate, which is the one
  // that shows unless it fails to load. A Pexels photo needs no credit line
  // (it is listed on /photo-credits all the same).
  const heroCredit = first && first.source !== "pexels" && first.credit ? first : null;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `Beach clubs in ${label}`,
    itemListElement: clubs.slice(0, 50).map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `https://sunbedly.com/club/${c.slug}`,
      name: c.name,
    })),
  };

  return (
    <div className="min-h-dvh bg-[#F2EFE9]">
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Nav />
      <main>
        <section className="relative h-[36dvh] min-h-[240px] w-full overflow-hidden">
          <CoverImage
            sources={heroSources}
            alt={first?.alt ?? label}
            srcSet={first?.srcSet ?? null}
            sizes="100vw"
            width={first?.width ?? undefined}
            height={first?.height ?? undefined}
            loading="eager"
            fallback={null}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1C2430]/75 via-[#1C2430]/10 to-transparent" />
          {heroCredit ? (
            <span className="li-mono absolute right-3 top-3 max-w-[70%] truncate rounded bg-[#1C2430]/60 px-1.5 py-0.5 text-[10px] text-[#F2EFE9]">
              {heroCredit.creditUrl ? (
                <a href={heroCredit.creditUrl} target="_blank" rel="noopener noreferrer nofollow" className="underline">
                  {t("photo_credit", { name: heroCredit.credit ?? "" })}
                </a>
              ) : (
                t("photo_credit", { name: heroCredit.credit ?? "" })
              )}
              {heroCredit.license ? (
                <>
                  {" / "}
                  {heroCredit.licenseUrl ? (
                    <a href={heroCredit.licenseUrl} target="_blank" rel="license noopener noreferrer nofollow" className="underline">
                      {heroCredit.license}
                    </a>
                  ) : (
                    heroCredit.license
                  )}
                </>
              ) : null}
            </span>
          ) : null}
          <div className="absolute inset-x-0 bottom-0">
            <div className="mx-auto max-w-6xl px-5 pb-6">
              <Link
                to="/clubs"
                className="li-mono text-[11px] text-[#F2EFE9]/80 hover:text-[#F2EFE9]"
              >
                ← {t("back_to_clubs")}
              </Link>
              <h1 className="li-serif mt-2 text-3xl font-normal  text-[#F2EFE9] md:text-5xl">
                {label}
              </h1>
              <p className="li-mono mt-1 text-[12px] text-[#F2EFE9]/85">
                {clubs.length} {t("clubs_found")}
                {country ? ` / ${country}` : ""}
              </p>
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-6xl px-5 pb-20 pt-8">
          {clubs.length === 0 ? (
            <div className="rounded-xl border border-dashed border-[#1C2430]/25 p-12 text-center">
              <p className="text-sm text-[#5B6472]">{t("no_results")}</p>
            </div>
          ) : (
            <>
              {/* Region pages carry every club on the coast, which is what
                  makes them worth indexing, but they have no filters of their
                  own. Rather than build a second filter UI here, send anyone
                  who wants to narrow it down into the catalogue with this
                  region already selected. */}
              {clubs.length > 12 ? (
                <div className="mb-6 flex justify-end">
                  <Link
                    to="/clubs"
                    search={{ region: key }}
                    className="li-mono rounded-full border border-[#1C2430]/25 px-4 py-2 text-[12px] text-[#1C2430] transition-colors hover:border-[#2C4FD6] hover:text-[#2C4FD6]"
                  >
                    {t("filters_show")} &amp; {t("sort_label").toLowerCase()} →
                  </Link>
                </div>
              ) : null}
              <ClubsView clubs={clubs} />
            </>
          )}
        </div>
        {/* "Beach clubs in Sicily" is not what anyone types. "Dog-friendly
            beach clubs in Sicily" is, and until now that answer only existed
            as a filter chip no search engine could land on. */}
        {key && clubs.length > 0 ? (
          <div className="mx-auto max-w-6xl px-5 pb-12">
            <ThemeLinks clubs={clubs} regionKey={key} />
          </div>
        ) : null}
        {facts ? (
          <div className="mx-auto max-w-6xl px-5 pb-16">
            <RegionAnswers facts={facts} place={label} />
          </div>
        ) : null}
      </main>
      <Footer />
    </div>
  );
}
